import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { RangePicker } = DatePicker;
const { Text } = Typography;

type HotelProperty = {
  id: number;
  name: string;
};

type HotelRoomType = {
  id: number;
  name: string;
  property_id: number;
  default_rate?: number | null;
};

type Currency = {
  id: number;
  name: string;
  code: string;
  symbol: string;
};

type ApiRatePlan = {
  id: number;
  property_id: number;
  property_name?: string | null;
  name: string;
  code: string;
  currency_id: number;
  currency_code?: string | null;
  meal_plan?: string | null;
  is_refundable?: number | boolean | null;
  cancellation_policy?: string | null;
  min_stay?: number | null;
  max_stay?: number | null;
  advance_booking_min_days?: number | null;
  advance_booking_max_days?: number | null;
  is_active?: number | boolean | null;
};

type RatePlanRecord = {
  id: number;
  propertyId: number;
  propertyName: string;
  name: string;
  code: string;
  currencyId: number;
  currencyCode?: string | null;
  mealPlan: string;
  isRefundable: boolean;
  cancellationPolicy?: string | null;
  minStay?: number | null;
  maxStay?: number | null;
  advanceBookingMinDays?: number | null;
  advanceBookingMaxDays?: number | null;
  isActive: boolean;
};

type ApiRateCalendarEntry = {
  id: number;
  property_id: number;
  rate_plan_id: number;
  room_type_id: number;
  room_type_name?: string | null;
  rate_date: string;
  price: number | string;
  min_stay?: number | null;
  max_stay?: number | null;
  stop_sell?: number | boolean | null;
  close_to_arrival?: number | boolean | null;
  close_to_departure?: number | boolean | null;
};

type RateCalendarEntry = {
  id: number;
  propertyId: number;
  ratePlanId: number;
  roomTypeId: number;
  roomTypeName: string;
  rateDate: string;
  price: number;
  minStay?: number | null;
  maxStay?: number | null;
  stopSell: boolean;
  closeToArrival: boolean;
  closeToDeparture: boolean;
};

type PlanFormValues = {
  propertyId: number;
  name: string;
  code: string;
  currencyId: number;
  mealPlan: string;
  isRefundable: boolean;
  cancellationPolicy?: string;
  minStay?: number;
  maxStay?: number;
  advanceBookingMinDays?: number;
  advanceBookingMaxDays?: number;
  isActive: boolean;
};

type PlanRoomTypeRate = {
  roomTypeId: number;
  roomTypeName: string;
  baseRate: number;
  extraAdultRate: number;
  extraChildRate: number;
  minStay?: number | null;
  maxStay?: number | null;
  isActive: boolean;
};

type CalendarFilters = {
  propertyId: number | "all";
  ratePlanId: number | "all";
  roomTypeId: number | "all";
  dateRange: [Dayjs, Dayjs] | null;
};

type BulkRateValues = {
  price: number;
  minStay?: number;
  maxStay?: number;
  stopSell?: boolean;
  closeToArrival?: boolean;
  closeToDeparture?: boolean;
};

const mealPlanLabels: Record<string, string> = {
  room_only: "Chambre seule",
  breakfast: "Petit dejeuner",
  half_board: "Demi-pension",
  full_board: "Pension complete",
  all_inclusive: "Tout inclus",
};

const RatesTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [roomTypes, setRoomTypes] = useState<HotelRoomType[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlanRecord[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [rateCalendarEntries, setRateCalendarEntries] = useState<
    RateCalendarEntry[]
  >([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<RatePlanRecord | null>(null);
  const [planForm] = Form.useForm<PlanFormValues>();
  const [bulkForm] = Form.useForm<BulkRateValues>();
  const [planRoomRates, setPlanRoomRates] = useState<PlanRoomTypeRate[]>([]);
  const [planRoomRatesLoading, setPlanRoomRatesLoading] = useState(false);
  const [planFilters, setPlanFilters] = useState<{
    propertyId: number | "all";
    isActive: "all" | "true" | "false";
  }>({
    propertyId: "all",
    isActive: "all",
  });
  const [calendarFilters, setCalendarFilters] = useState<CalendarFilters>({
    propertyId: "all",
    ratePlanId: "all",
    roomTypeId: "all",
    dateRange: [dayjs(), dayjs().add(14, "day")],
  });

  const planPropertyId = Form.useWatch("propertyId", planForm);
  const editingPlanId = editingPlan?.id;

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const hasProperties = properties.length > 0;
  const canCreateRatePlan = Boolean(
    hasPermission?.("Hotellerie.Tarifs.PlansTarifaires.create")
  );
  const canEditRatePlan = Boolean(
    hasPermission?.("Hotellerie.Tarifs.PlansTarifaires.edit")
  );
  const canManageCalendar = Boolean(
    hasPermission?.("Hotellerie.Tarifs.Calendrier.create") ||
      hasPermission?.("Hotellerie.Tarifs.Calendrier.edit")
  );

  const formatCurrency = useCallback(
    (value: number, currencyCode?: string | null) => {
      const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
      return currencyCode ? `${formatted} ${currencyCode}` : formatted;
    },
    []
  );

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchCurrencies = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = {
        page: 1,
        limit: 200,
      };
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/currencies`, {
        params,
      });
      setCurrencies(response.data.currencies || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching currencies:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des devises"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchRoomTypes = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (calendarFilters.propertyId !== "all") {
        params.property_id = calendarFilters.propertyId;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/room-types`, {
        params,
      });
      if (response.data.success) {
        setRoomTypes(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching room types:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des types de chambres"
      );
    }
  }, [calendarFilters.propertyId, requiresCompanySelection, selectedCompany]);

  const buildPlanRoomRates = useCallback(
    async (propertyId: number, ratePlanId?: number) => {
      if (requiresCompanySelection || !propertyId) {
        setPlanRoomRates([]);
        return;
      }
      setPlanRoomRatesLoading(true);
      try {
        const params: Record<string, string | number> = { property_id: propertyId };
        if (selectedCompany) {
          params.company_id = selectedCompany;
        }
        const response = await axios.get(`${API_CONFIG.API_URL}/hotel/room-types`, {
          params,
        });
        const roomTypesData: HotelRoomType[] = response.data.data || [];

        const existingRatesMap = new Map<number, any>();
        if (ratePlanId) {
          const rateParams = selectedCompany ? { company_id: selectedCompany } : {};
          const rateResponse = await axios.get(
            `${API_CONFIG.API_URL}/hotel/rate-plans/${ratePlanId}/room-types`,
            { params: rateParams }
          );
          (rateResponse.data.data || []).forEach((row: any) => {
            existingRatesMap.set(Number(row.room_type_id), row);
          });
        }

        const mapped = roomTypesData.map((roomType) => {
          const existing = existingRatesMap.get(Number(roomType.id));
          return {
            roomTypeId: Number(roomType.id),
            roomTypeName: roomType.name,
            baseRate: Number(existing?.base_rate ?? roomType.default_rate ?? 0),
            extraAdultRate: Number(existing?.extra_adult_rate ?? 0),
            extraChildRate: Number(existing?.extra_child_rate ?? 0),
            minStay: existing?.min_stay ?? null,
            maxStay: existing?.max_stay ?? null,
            isActive: existing ? Boolean(existing.is_active) : true,
          };
        });

        setPlanRoomRates(mapped);
      } catch (error: any) {
        console.error("[HOTEL] Error fetching plan room rates:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des tarifs par type"
        );
      } finally {
        setPlanRoomRatesLoading(false);
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const savePlanRoomRates = useCallback(
    async (ratePlanId: number) => {
      if (requiresCompanySelection || planRoomRates.length === 0) {
        return;
      }
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        room_types: planRoomRates.map((rate) => ({
          room_type_id: rate.roomTypeId,
          base_rate: rate.baseRate,
          extra_adult_rate: rate.extraAdultRate,
          extra_child_rate: rate.extraChildRate,
          min_stay: rate.minStay ?? null,
          max_stay: rate.maxStay ?? null,
          is_active: rate.isActive ? 1 : 0,
        })),
      };

      await axios.put(
        `${API_CONFIG.API_URL}/hotel/rate-plans/${ratePlanId}/room-types`,
        payload
      );
    },
    [planRoomRates, requiresCompanySelection, selectedCompany]
  );

  const fetchRatePlans = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingPlans(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (planFilters.propertyId !== "all") {
        params.property_id = planFilters.propertyId;
      }
      if (planFilters.isActive !== "all") {
        params.is_active = planFilters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rate-plans`, {
        params,
      });
      if (response.data.success) {
        const mapped = (response.data.data || []).map((row: ApiRatePlan) => ({
          id: row.id,
          propertyId: Number(row.property_id),
          propertyName: row.property_name || `Propriete #${row.property_id}`,
          name: row.name,
          code: row.code,
          currencyId: Number(row.currency_id),
          currencyCode: row.currency_code || null,
          mealPlan: row.meal_plan || "room_only",
          isRefundable: Boolean(row.is_refundable),
          cancellationPolicy: row.cancellation_policy || null,
          minStay: row.min_stay ?? null,
          maxStay: row.max_stay ?? null,
          advanceBookingMinDays: row.advance_booking_min_days ?? null,
          advanceBookingMaxDays: row.advance_booking_max_days ?? null,
          isActive: Boolean(row.is_active),
        }));
        setRatePlans(mapped);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching rate plans:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des plans tarifaires"
      );
    } finally {
      setLoadingPlans(false);
    }
  }, [planFilters.isActive, planFilters.propertyId, requiresCompanySelection, selectedCompany]);

  const fetchRateCalendar = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingCalendar(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (calendarFilters.propertyId !== "all") {
        params.property_id = calendarFilters.propertyId;
      }
      if (calendarFilters.ratePlanId !== "all") {
        params.rate_plan_id = calendarFilters.ratePlanId;
      }
      if (calendarFilters.roomTypeId !== "all") {
        params.room_type_id = calendarFilters.roomTypeId;
      }
      if (calendarFilters.dateRange) {
        params.start_date = calendarFilters.dateRange[0].format("YYYY-MM-DD");
        params.end_date = calendarFilters.dateRange[1].format("YYYY-MM-DD");
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rate-calendar`, {
        params,
      });
      if (response.data.success) {
        const mapped = (response.data.data || []).map(
          (row: ApiRateCalendarEntry) => ({
            id: row.id,
            propertyId: Number(row.property_id),
            ratePlanId: Number(row.rate_plan_id),
            roomTypeId: Number(row.room_type_id),
            roomTypeName: row.room_type_name || `Type #${row.room_type_id}`,
            rateDate: row.rate_date,
            price: Number(row.price || 0),
            minStay: row.min_stay ?? null,
            maxStay: row.max_stay ?? null,
            stopSell: Boolean(row.stop_sell),
            closeToArrival: Boolean(row.close_to_arrival),
            closeToDeparture: Boolean(row.close_to_departure),
          })
        );
        setRateCalendarEntries(mapped);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching rate calendar:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement du calendrier"
      );
    } finally {
      setLoadingCalendar(false);
    }
  }, [calendarFilters, requiresCompanySelection, selectedCompany]);

  useEffect(() => {
    fetchProperties();
    fetchCurrencies();
  }, [fetchCurrencies, fetchProperties]);

  useEffect(() => {
    fetchRatePlans();
  }, [fetchRatePlans]);

  useEffect(() => {
    fetchRoomTypes();
  }, [fetchRoomTypes]);

  useEffect(() => {
    fetchRateCalendar();
  }, [fetchRateCalendar]);

  useEffect(() => {
    if (!planModalOpen) {
      return;
    }
    if (planPropertyId) {
      buildPlanRoomRates(planPropertyId, editingPlanId);
    } else {
      setPlanRoomRates([]);
    }
  }, [buildPlanRoomRates, editingPlanId, planModalOpen, planPropertyId]);

  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanRoomRates([]);
    planForm.resetFields();
    const defaultPropertyId =
      planFilters.propertyId !== "all"
        ? planFilters.propertyId
        : properties[0]?.id;
    planForm.setFieldsValue({
      propertyId: defaultPropertyId,
      mealPlan: "room_only",
      isRefundable: true,
      isActive: true,
      advanceBookingMinDays: 0,
    });
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan: RatePlanRecord) => {
    setEditingPlan(plan);
    setPlanRoomRates([]);
    planForm.setFieldsValue({
      propertyId: plan.propertyId,
      name: plan.name,
      code: plan.code,
      currencyId: plan.currencyId,
      mealPlan: plan.mealPlan,
      isRefundable: plan.isRefundable,
      cancellationPolicy: plan.cancellationPolicy || undefined,
      minStay: plan.minStay ?? undefined,
      maxStay: plan.maxStay ?? undefined,
      advanceBookingMinDays: plan.advanceBookingMinDays ?? 0,
      advanceBookingMaxDays: plan.advanceBookingMaxDays ?? undefined,
      isActive: plan.isActive,
    });
    setPlanModalOpen(true);
  };

  const handleSavePlan = async () => {
    const values = await planForm.validateFields();
    if (!editingPlan && !canCreateRatePlan) {
      message.warning("Vous n'avez pas les droits pour creer un plan.");
      return;
    }
    if (editingPlan && !canEditRatePlan) {
      message.warning("Vous n'avez pas les droits pour modifier ce plan.");
      return;
    }

    try {
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        name: values.name,
        code: values.code,
        currency_id: values.currencyId,
        meal_plan: values.mealPlan,
        is_refundable: values.isRefundable,
        cancellation_policy: values.cancellationPolicy || null,
        min_stay: values.minStay ?? null,
        max_stay: values.maxStay ?? null,
        advance_booking_min_days: values.advanceBookingMinDays ?? 0,
        advance_booking_max_days: values.advanceBookingMaxDays ?? null,
        is_active: values.isActive,
      };

      if (editingPlan) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/rate-plans/${editingPlan.id}`,
          payload
        );
        if (response.data.success) {
          try {
            await savePlanRoomRates(editingPlan.id);
          } catch (error: any) {
            console.error("[HOTEL] Error saving plan room rates:", error);
            message.warning(
              "Plan enregistre, mais les tarifs par type n'ont pas ete sauvegardes."
            );
          }
          message.success("Plan tarifaire mis a jour.");
          setPlanModalOpen(false);
          setEditingPlan(null);
          fetchRatePlans();
        }
      } else {
        const response = await axios.post(`${API_CONFIG.API_URL}/hotel/rate-plans`, {
          ...payload,
          property_id: values.propertyId,
        });
        if (response.data.success) {
          const createdId = response.data.data?.id;
          if (createdId) {
            try {
              await savePlanRoomRates(createdId);
            } catch (error: any) {
              console.error("[HOTEL] Error saving plan room rates:", error);
              message.warning(
                "Plan cree, mais les tarifs par type n'ont pas ete sauvegardes."
              );
            }
          }
          message.success("Plan tarifaire cree.");
          setPlanModalOpen(false);
          fetchRatePlans();
        }
      }
    } catch (error: any) {
      console.error("[HOTEL] Error saving rate plan:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde du plan tarifaire"
      );
    }
  };

  const updatePlanRoomRate = (
    roomTypeId: number,
    patch: Partial<PlanRoomTypeRate>
  ) => {
    setPlanRoomRates((prev) =>
      prev.map((rate) =>
        rate.roomTypeId === roomTypeId ? { ...rate, ...patch } : rate
      )
    );
  };

  const handleBulkApply = async () => {
    if (!canManageCalendar) {
      message.warning("Vous n'avez pas les droits pour modifier le calendrier.");
      return;
    }
    if (
      calendarFilters.propertyId === "all" ||
      calendarFilters.ratePlanId === "all" ||
      calendarFilters.roomTypeId === "all" ||
      !calendarFilters.dateRange
    ) {
      message.warning("Selectionnez la propriete, le plan, le type et les dates.");
      return;
    }

    const values = await bulkForm.validateFields();
    const [start, end] = calendarFilters.dateRange;
    setBulkLoading(true);

    try {
      const params: Record<string, string | number> = {
        property_id: calendarFilters.propertyId,
        rate_plan_id: calendarFilters.ratePlanId,
        room_type_id: calendarFilters.roomTypeId,
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
      };
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      const existingResponse = await axios.get(
        `${API_CONFIG.API_URL}/hotel/rate-calendar`,
        { params }
      );
      const existingEntries: ApiRateCalendarEntry[] =
        existingResponse.data.data || [];
      const existingMap = new Map<string, number>();
      existingEntries.forEach((entry) => {
        existingMap.set(entry.rate_date, entry.id);
      });

      let cursor = start.startOf("day");
      while (cursor.isSame(end, "day") || cursor.isBefore(end, "day")) {
        const rateDate = cursor.format("YYYY-MM-DD");
        const payload = {
          ...(selectedCompany ? { company_id: selectedCompany } : {}),
          property_id: calendarFilters.propertyId,
          rate_plan_id: calendarFilters.ratePlanId,
          room_type_id: calendarFilters.roomTypeId,
          rate_date: rateDate,
          price: values.price,
          min_stay: values.minStay ?? null,
          max_stay: values.maxStay ?? null,
          stop_sell: values.stopSell ? 1 : 0,
          close_to_arrival: values.closeToArrival ? 1 : 0,
          close_to_departure: values.closeToDeparture ? 1 : 0,
        };

        const existingId = existingMap.get(rateDate);
        if (existingId) {
          await axios.put(
            `${API_CONFIG.API_URL}/hotel/rate-calendar/${existingId}`,
            payload
          );
        } else {
          await axios.post(`${API_CONFIG.API_URL}/hotel/rate-calendar`, payload);
        }

        cursor = cursor.add(1, "day");
      }

      message.success("Calendrier mis a jour.");
      fetchRateCalendar();
    } catch (error: any) {
      console.error("[HOTEL] Error applying bulk rates:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la mise a jour du calendrier"
      );
    } finally {
      setBulkLoading(false);
    }
  };

  const planColumns: ColumnsType<RatePlanRecord> = [
    {
      title: "Plan",
      dataIndex: "name",
      key: "name",
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value}</Text>
          <Text type="secondary">{record.code}</Text>
        </Space>
      ),
    },
    {
      title: "Propriete",
      dataIndex: "propertyName",
      key: "propertyName",
    },
    {
      title: "Regime",
      dataIndex: "mealPlan",
      key: "mealPlan",
      render: (value: string) => mealPlanLabels[value] || value,
    },
    {
      title: "Remboursable",
      dataIndex: "isRefundable",
      key: "isRefundable",
      render: (value: boolean) => (
        <Tag color={value ? "green" : "red"}>{value ? "Oui" : "Non"}</Tag>
      ),
    },
    {
      title: "Regles de sejour",
      key: "stayRules",
      render: (_value, record) => (
        <Text type="secondary">
          {record.minStay ?? "-"} / {record.maxStay ?? "-"}
        </Text>
      ),
    },
    {
      title: "Statut",
      dataIndex: "isActive",
      key: "isActive",
      render: (value: boolean) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) =>
        canEditRatePlan ? (
          <Button type="link" onClick={() => openEditPlan(record)}>
            Modifier
          </Button>
        ) : null,
    },
  ];

  const calendarColumns: ColumnsType<RateCalendarEntry> = [
    {
      title: "Date",
      dataIndex: "rateDate",
      key: "rateDate",
      render: (value: string) => dayjs(value).format("DD/MM/YYYY"),
    },
    {
      title: "Type de chambre",
      dataIndex: "roomTypeName",
      key: "roomTypeName",
    },
    {
      title: "Prix",
      dataIndex: "price",
      key: "price",
      align: "right",
      render: (value: number, record) =>
        formatCurrency(
          value,
          ratePlans.find((plan) => plan.id === record.ratePlanId)?.currencyCode
        ),
    },
    {
      title: "Sejour min",
      dataIndex: "minStay",
      key: "minStay",
      render: (value: number | null | undefined) => value ?? "-",
    },
    {
      title: "Sejour max",
      dataIndex: "maxStay",
      key: "maxStay",
      render: (value: number | null | undefined) => value ?? "-",
    },
    {
      title: "Restrictions",
      key: "restrictions",
      render: (_value, record) => (
        <Space size={4} wrap>
          {record.stopSell ? (
            <Tag color="red">Fermeture</Tag>
          ) : (
            <Tag color="green">Ouvert</Tag>
          )}
          {record.closeToArrival ? <Tag color="orange">CTA</Tag> : null}
          {record.closeToDeparture ? <Tag color="orange">CTD</Tag> : null}
        </Space>
      ),
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux tarifs."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title="Plans tarifaires"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchRatePlans}
              loading={loadingPlans}
            >
              Rafraichir
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreatePlan}
              disabled={!canCreateRatePlan || !hasProperties}
            >
              Nouveau plan
            </Button>
          </Space>
        }
      >
        {!hasProperties ? (
          <Alert
            message="Aucune propriete configuree"
            description="Creez une propriete dans l'onglet Configuration pour activer la creation de plans tarifaires."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Form.Item label="Propriete">
                <Select
                  value={planFilters.propertyId}
                  onChange={(value) =>
                    setPlanFilters((prev) => ({ ...prev, propertyId: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...properties.map((property) => ({
                      value: property.id,
                      label: property.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Actif">
                <Select
                  value={planFilters.isActive}
                  onChange={(value) =>
                    setPlanFilters((prev) => ({ ...prev, isActive: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "true", label: "Actif" },
                    { value: "false", label: "Inactif" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          columns={planColumns}
          dataSource={ratePlans}
          rowKey="id"
          loading={loadingPlans}
          pagination={{ pageSize: 6 }}
        />
      </Card>

      <Card
        title="Calendrier tarifaire"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchRateCalendar}
            loading={loadingCalendar}
          >
            Rafraichir
          </Button>
        }
      >
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Form.Item label="Propriete">
                <Select
                  value={calendarFilters.propertyId}
                  onChange={(value) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      propertyId: value,
                      ratePlanId: "all",
                      roomTypeId: "all",
                    }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...properties.map((property) => ({
                      value: property.id,
                      label: property.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Plan tarifaire">
                <Select
                  value={calendarFilters.ratePlanId}
                  onChange={(value) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      ratePlanId: value,
                    }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...ratePlans
                      .filter((plan) =>
                        calendarFilters.propertyId === "all"
                          ? true
                          : plan.propertyId === calendarFilters.propertyId
                      )
                      .map((plan) => ({
                        value: plan.id,
                        label: plan.name,
                      })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Type de chambre">
                <Select
                  value={calendarFilters.roomTypeId}
                  onChange={(value) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      roomTypeId: value,
                    }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...roomTypes.map((roomType) => ({
                      value: roomType.id,
                      label: roomType.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Periode">
                <RangePicker
                  value={calendarFilters.dateRange ?? null}
                  onChange={(range) =>
                    setCalendarFilters((prev) => ({
                      ...prev,
                      dateRange:
                        range && range[0] && range[1]
                          ? [range[0], range[1]]
                          : null,
                    }))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Card
          type="inner"
          title="Mise a jour en masse"
          extra={
            <Button
              type="primary"
              onClick={handleBulkApply}
              loading={bulkLoading}
              disabled={!canManageCalendar}
            >
              Appliquer
            </Button>
          }
        >
          <Form form={bulkForm} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Form.Item
                  label="Prix"
                  name="price"
                  rules={[{ required: true, message: "Prix requis" }]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Sejour min" name="minStay">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Sejour max" name="maxStay">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Fermeture" name="stopSell" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  label="Ferme a l'arrivee"
                  name="closeToArrival"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  label="Ferme au depart"
                  name="closeToDeparture"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {rateCalendarEntries.length === 0 && !loadingCalendar ? (
          <Empty description="Aucune entree pour cette periode." />
        ) : (
          <Table
            columns={calendarColumns}
            dataSource={rateCalendarEntries}
            rowKey="id"
            loading={loadingCalendar}
            pagination={{ pageSize: 7 }}
          />
        )}
      </Card>

      <Modal
        title={editingPlan ? "Modifier plan tarifaire" : "Nouveau plan tarifaire"}
        open={planModalOpen}
        onCancel={() => {
          setPlanModalOpen(false);
          setEditingPlan(null);
          setPlanRoomRates([]);
        }}
        onOk={handleSavePlan}
        okText="Enregistrer"
      >
        <Form form={planForm} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select
              disabled={Boolean(editingPlan)}
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: "Code requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Devise"
                name="currencyId"
                rules={[{ required: true, message: "Devise requise" }]}
              >
                <Select
                  options={currencies.map((currency) => ({
                    value: currency.id,
                    label: `${currency.code} - ${currency.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Regime"
                name="mealPlan"
                rules={[{ required: true }]}
              >
                <Select
                  options={Object.entries(mealPlanLabels).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text strong>Tarifs par type de chambre</Text>
            <Table
              size="small"
              rowKey="roomTypeId"
              dataSource={planRoomRates}
              loading={planRoomRatesLoading}
              pagination={false}
              locale={{ emptyText: "Aucun type de chambre disponible." }}
              columns={[
                {
                  title: "Type de chambre",
                  dataIndex: "roomTypeName",
                  key: "roomTypeName",
                },
                {
                  title: "Montant",
                  dataIndex: "baseRate",
                  key: "baseRate",
                  render: (value: number, record: PlanRoomTypeRate) => (
                    <InputNumber
                      min={0}
                      value={value}
                      style={{ width: "100%" }}
                      onChange={(next) =>
                        updatePlanRoomRate(record.roomTypeId, {
                          baseRate: typeof next === "number" ? next : 0,
                        })
                      }
                    />
                  ),
                },
              ]}
            />
          </Space>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Sejour min" name="minStay">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Sejour max" name="maxStay">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Reservation min (jours)" name="advanceBookingMinDays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Reservation max (jours)" name="advanceBookingMaxDays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Remboursable" name="isRefundable" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Politique d'annulation" name="cancellationPolicy">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Actif" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default RatesTab;
